import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Home, DollarSign, Bed, Bath, Square, MapPin, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentRequestModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    area_interested: '',
    home_size_sqft: '',
    bedrooms: '',
    bathrooms: '',
    price_range_min: '',
    price_range_max: '',
    pre_approved: '',
    pre_approved_amount: '',
    preferred_contact_method: '',
    preferred_time: '',
    additional_notes: '',
    // NEW FIELDS
    property_type: 'buying',  // buying or selling
    num_agents_requested: 1,   // 1, 2, 3, or 4+
    urgency: 'flexible'        // immediate, 2_weeks, 1_month, flexible
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.area_interested || !formData.pre_approved || !formData.preferred_contact_method || !formData.property_type || !formData.num_agents_requested || !formData.urgency) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/agent-requests`, formData);
      setSubmitted(true);
      toast.success('Your request has been submitted! We\'ll contact you soon.');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      area_interested: '',
      home_size_sqft: '',
      bedrooms: '',
      bathrooms: '',
      price_range_min: '',
      price_range_max: '',
      pre_approved: '',
      pre_approved_amount: '',
      preferred_contact_method: '',
      preferred_time: '',
      additional_notes: '',
      property_type: 'buying',
      num_agents_requested: 1,
      urgency: 'flexible'
    });
    setSubmitted(false);
    onClose();
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Request Submitted!</h3>
            <p className="text-gray-600 mb-4">
              We've received your information and will connect you with {formData.num_agents_requested} carefully vetted Dallas-Fort Worth real estate {formData.num_agents_requested === 1 ? 'agent' : 'agents'}.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <strong>What happens next:</strong> Our agents are monitored for response time and professionalism. If they don't respond quickly or meet our standards, they're removed from our network.
            </p>
            <p className="text-xs text-gray-500 mb-6 italic">
              Disclaimer: Its-Sold.com™ is not affiliated with, sponsored by, or representing any real estate brokerage firm. We provide a referral service to connect buyers and sellers with independent licensed agents and brokers.
            </p>
            <Button onClick={handleClose} className="bg-orange-600 hover:bg-orange-700">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Find a Licensed DFW Real Estate Agent</DialogTitle>
          <DialogDescription>
            Tell us what you're looking for and we'll connect you with experienced Dallas-Fort Worth real estate agents and brokers.
          </DialogDescription>
          <p className="text-xs text-gray-500 mt-2 italic border-l-2 border-orange-300 pl-3">
            <strong>Disclaimer:</strong> Its-Sold.com™ is not affiliated with, sponsored by, or representing any real estate brokerage firm. We provide a free referral service only.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* VETTED NETWORK NOTICE */}
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-orange-900">
                  <strong>Exclusive Vetted Network:</strong> We carefully select and monitor our agents. 
                  If agents don't respond quickly or perform poorly, they're removed from our network. 
                  Your satisfaction is our priority.
                </p>
              </div>
            </div>
          </div>

          {/* NEW: Request Type & Preferences */}
          <div className="pt-4 border-t">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Home className="w-5 h-5 mr-2 text-orange-600" />
              Your Request
            </h4>

            <div className="space-y-4">
              {/* Property Type */}
              <div>
                <Label htmlFor="property-type" className="required">Are you buying or selling? *</Label>
                <Select value={formData.property_type} onValueChange={(value) => handleChange('property_type', value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buying">🏠 I'm looking to buy</SelectItem>
                    <SelectItem value="selling">📋 I'm selling my property</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Number of Agents */}
              <div>
                <Label htmlFor="num-agents" className="required">How many agents would you like us to contact on your behalf? *</Label>
                <p className="text-xs text-gray-500 mb-2">More agents = more options, but you decide!</p>
                <Select value={String(formData.num_agents_requested)} onValueChange={(value) => handleChange('num_agents_requested', parseInt(value))} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Agent</SelectItem>
                    <SelectItem value="2">2 Agents</SelectItem>
                    <SelectItem value="3">3 Agents (Recommended)</SelectItem>
                    <SelectItem value="4">4+ Agents (Maximum Options)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Urgency/Timeline */}
              <div>
                <Label htmlFor="urgency" className="required">What's your timeline? *</Label>
                <p className="text-xs text-gray-500 mb-2">This helps agents know how hot your lead is</p>
                <Select value={formData.urgency} onValueChange={(value) => handleChange('urgency', value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">🔴 Ready Now / Immediate</SelectItem>
                    <SelectItem value="2_weeks">🟡 Within 2 Weeks</SelectItem>
                    <SelectItem value="1_month">🟢 Within 1 Month</SelectItem>
                    <SelectItem value="flexible">⚪ Flexible / Just Exploring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="pt-4 border-t">
            <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="required">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="required">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="phone" className="required">Phone Number *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(214) 555-0100"
              required
            />
          </div>

          {/* Property Details */}
          <div className="pt-4 border-t">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Home className="w-5 h-5 mr-2 text-orange-600" />
              Property Preferences
            </h4>

            <div className="space-y-4">
              <div>
                <Label htmlFor="area" className="required">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Area/City Interested In *
                </Label>
                <Input
                  id="area"
                  value={formData.area_interested}
                  onChange={(e) => handleChange('area_interested', e.target.value)}
                  placeholder="Frisco, Plano, Dallas, etc."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sqft">
                    <Square className="w-4 h-4 inline mr-1" />
                    Home Size (sq ft)
                  </Label>
                  <Input
                    id="sqft"
                    type="number"
                    value={formData.home_size_sqft}
                    onChange={(e) => handleChange('home_size_sqft', e.target.value)}
                    placeholder="2000"
                  />
                </div>

                <div>
                  <Label htmlFor="bedrooms">
                    <Bed className="w-4 h-4 inline mr-1" />
                    Bedrooms
                  </Label>
                  <Select value={formData.bedrooms} onValueChange={(value) => handleChange('bedrooms', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5+">5+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="bathrooms">
                  <Bath className="w-4 h-4 inline mr-1" />
                  Bathrooms
                </Label>
                <Select value={formData.bathrooms} onValueChange={(value) => handleChange('bathrooms', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="1.5">1.5</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="2.5">2.5</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="3.5">3.5</SelectItem>
                    <SelectItem value="4+">4+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Price Range
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="number"
                    value={formData.price_range_min}
                    onChange={(e) => handleChange('price_range_min', e.target.value)}
                    placeholder="Min ($)"
                  />
                  <Input
                    type="number"
                    value={formData.price_range_max}
                    onChange={(e) => handleChange('price_range_max', e.target.value)}
                    placeholder="Max ($)"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Financing */}
          <div className="pt-4 border-t">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-orange-600" />
              Financing Information
            </h4>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="pre-approved" className="required">Are you pre-approved for a loan? *</Label>
                <Select value={formData.pre_approved} onValueChange={(value) => handleChange('pre_approved', value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes, I'm pre-approved</SelectItem>
                    <SelectItem value="no">No, not yet</SelectItem>
                    <SelectItem value="cash">Cash buyer (no loan needed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Pre-approved Amount - Only show if yes */}
              {formData.pre_approved === 'yes' && (
                <div>
                  <Label htmlFor="pre-approved-amount">Pre-Approved Loan Amount</Label>
                  <Input
                    id="pre-approved-amount"
                    type="number"
                    value={formData.pre_approved_amount}
                    onChange={(e) => handleChange('pre_approved_amount', e.target.value)}
                    placeholder="$500,000"
                  />
                  <p className="text-xs text-gray-500 mt-1">This helps agents find properties within your budget</p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Preferences */}
          <div className="pt-4 border-t">
            <h4 className="font-semibold text-gray-900 mb-3">Contact Preferences</h4>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="contact-method" className="required">Preferred Contact Method *</Label>
                <Select value={formData.preferred_contact_method} onValueChange={(value) => handleChange('preferred_contact_method', value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">Text Message (SMS)</SelectItem>
                    <SelectItem value="any">Any method is fine</SelectItem>
                  </SelectContent>
                </Select>
                {/* Privacy Notice */}
                <p className="text-xs text-gray-500 mt-1">
                  Your contact info is <strong>never shared</strong> with third parties. All communications come directly from Its-Sold.com.
                </p>
              </div>

              <div>
                <Label htmlFor="preferred-time">Best Time to Contact</Label>
                <Select value={formData.preferred_time} onValueChange={(value) => handleChange('preferred_time', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                    <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                    <SelectItem value="anytime">Anytime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <textarea
              id="notes"
              value={formData.additional_notes}
              onChange={(e) => handleChange('additional_notes', e.target.value)}
              placeholder="Any specific requirements or preferences..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows="3"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AgentRequestModal;
